/**
 * AI Chat Pinia store。
 *
 * <p>本文件仅作为"薄入口"：定义 {@link useAiChatStore}，把状态组合起来。
 * 实际逻辑被拆分到以下子模块（{@code ./aiChat/}）：</p>
 *
 * <ul>
 *   <li>{@link ./aiChat/types} - 类型、常量、id 工具</li>
 *   <li>{@link ./aiChat/context} - 子 composable 共享的上下文类型</li>
 *   <li>{@link ./aiChat/timeline} - 时间线片段操作</li>
 *   <li>{@link ./aiChat/messages} - 服务端消息 → UI 映射</li>
 *   <li>{@link ./aiChat/detectors} - 从 tool 结果检测待办</li>
 *   <li>{@link ./aiChat/userActionQueue} - 统一用户行动队列 composable</li>
 *   <li>{@link ./aiChat/streamEvents} - SSE 事件分发</li>
 *   <li>{@link ./aiChat/useMessages} - 消息列表管理 composable</li>
 *   <li>{@link ./aiChat/useSessionLifecycle} - 会话生命周期 composable</li>
 *   <li>{@link ./aiChat/useStreamOrchestrator} - 流式编排 composable</li>
 *   <li>{@link ./aiChat/usePendingConfirmations} - 待确认工具调用 composable</li>
 *   <li>{@link ./aiChat/usePendingUserQuestions} - 待提问 composable</li>
 * </ul>
 */
import { defineStore } from 'pinia'
import { registerTenantScopedReset } from '@/stores/_tenantScopedRegistry'
import { computed, ref } from 'vue'
import type { AiStreamStateView } from '@/api/aiChat'
import { aiChatApi } from '@/api/aiChat'
import { useAiPlanStore } from '@/stores/aiPlan'
import { useAiSessionsStore } from '@/stores/aiSessions'
import type {
  PendingGatewayConfirmation,
  PendingUserQuestion
} from './aiChat/types'
import type { AiChatContext } from './aiChat/context'
import { createStreamRegistry } from './aiChat/context'
import { isSessionTaskOpen as sessionTaskIsOpen, sessionHasOpenUserAction } from './aiChat/sessionTask'
import { coalesceServerMessagesForUi } from './aiChat/messages'
import { useUserActionQueue } from './aiChat/userActionQueue'
import { useMessages } from './aiChat/useMessages'
import { useSessionLifecycle } from './aiChat/useSessionLifecycle'
import { useStreamOrchestrator } from './aiChat/useStreamOrchestrator'
import { usePendingConfirmations } from './aiChat/usePendingConfirmations'
import { usePendingUserQuestions } from './aiChat/usePendingUserQuestions'

/** 对外 re-export：从 aiChat 子模块透出，确保旧 import 路径仍然有效。 */
export {
  AI_CHAT_FILE_ONLY_MESSAGE
} from './aiChat/types'
export type {
  AiMessageRole,
  AiTimelineSegment,
  AiUiMessage,
  AiUserMaterialAttachment,
  PendingGatewayConfirmation,
  PendingUserQuestion
} from './aiChat/types'
export { parseNumericUserMessageId } from './aiChat/types'
export { syncFinalAssistantText, buildCompletedTurnView } from './aiChat/timeline'

export const useAiChatStore = defineStore('aiChat', () => {
  // ── 核心响应式 state ──
  const sessionId = ref<string | null>(null)
  /**
   * AI 任务在「已交接给用户行动队列（确认卡/提问卡）」期间为 true。
   * 业务上：此时 AI 任务在前端看来"未结束"，红色停止按钮应保持显示（与转动圆圈逻辑同构）；
   * 但用户需要能点击"发送"或继续输入来打断或追加指令，所以 `sending` 必须保持 false（不再守门）。
   */
  const awaitingUserAction = computed(() => {
    const sid = sessionId.value?.trim() ?? ''
    return sid !== '' && sessionHasOpenUserAction(sid, userActionQueue.value)
  })
  const lastError = ref<string | null>(null)
  /**
   * 上一回合异常停止后的可继续状态（提示条数据源）。
   * run_done 终态（CONTINUATION_REQUIRED/BUDGET_STOP/ERROR）或 stream-state 刷新恢复时置位；
   * 继续回合启动、发新消息、正常完成时清除。null = 无可继续中断。
   */
  const resumableStop = ref<import('./aiChat/types').ResumableStop | null>(null)
  /**
   * Plan store 实例(单例)。bridge 把它的方法暴露给 streamEvents，
   * 用于接收 plan_snapshot 事件。
   */
  const aiPlanStore = useAiPlanStore()
  const recoverableStreamState = ref<AiStreamStateView | null>(null)
  /** 仅覆盖「确认执行」HTTP 请求，不包含后续流式说明，避免按钮长时间显示执行中。 */
  const confirmationHttpPending = ref(false)
  /**
   * 按会话分桶的在途流注册表：每个会话独立登记自己的 SSE AbortController，
   * 支持多个会话同时跑任务互不干扰。sending / isSessionTaskOpen 均由此派生。
   */
  const streamsBySession = createStreamRegistry()
  /** 当前查看的会话是否有在途流（跨会话并行时只反映当前会话）。 */
  const sending = computed(() => streamsBySession.has(sessionId.value))

  // ── 消息管理 composable（持有 messages ref + 缓存 + watch） ──
  const {
    messages,
    messagesBySession,
    hasMessages,
    appendAssistantMessage,
    reconcileStaleStreamMessages
  } = useMessages(sessionId)

  // ── 用户行动队列 composable（确认卡 + 提问卡统一 FIFO 队列） ──
  const {
    userActionQueue,
    enqueueUserAction,
    clearUserActionQueue,
    clearPendingConfirmation,
    pendingConfirmation,
    pendingUserQuestion,
    pendingQuestionQueue,
    questionProgress,
    isLastQuestion,
    canGoPrevQuestion,
    currentStagedAnswer,
    goToNextQuestion,
    goToPrevQuestion,
    stageAnswer,
    getAllStagedAnswers,
    resetQuestionState,
    filterForActiveSession
    // 队列派生按当前会话过滤：避免其他会话的遗留卡占据队头，遮住当前会话实时送达的卡片。
  } = useUserActionQueue(() => sessionId.value?.trim() ?? '')

  // ── 跨会话派生 getters ──

  /** 仅当用户正在查看产生待确认的那条会话时返回对象，避免切换会话后仍显示 A 的确认面板 */
  const pendingConfirmationForActiveSession = computed(() => {
    return filterForActiveSession<PendingGatewayConfirmation>(
      pendingConfirmation.value,
      sessionId.value?.trim() ?? ''
    )
  })

  const pendingUserQuestionForActiveSession = computed(() => {
    return filterForActiveSession<PendingUserQuestion>(
      pendingUserQuestion.value,
      sessionId.value?.trim() ?? ''
    )
  })

  /**
   * Per-session equivalent of the composer Stop button
   * ({@code sending || awaitingUserAction}), including parked background streams.
   */
  function isSessionTaskOpen(targetSessionId: string | null | undefined): boolean {
    const sid = targetSessionId?.trim() ?? ''
    if (!sid) {
      return false
    }
    return sessionTaskIsOpen({
      sessionId: sid,
      hasLiveStream: streamsBySession.has(sid),
      userActionQueue: userActionQueue.value
    })
  }

  // ── 组装上下文（供子 composable 共享 state 与辅助函数） ──
  const ctx: AiChatContext = {
    sessionId,
    messages,
    sending,
    awaitingUserAction,
    lastError,
    recoverableStreamState,
    resumableStop,
    confirmationHttpPending,
    messagesBySession,
    userActionQueue,
    enqueueUserAction,
    clearUserActionQueue,
    clearPendingConfirmation,
    pendingConfirmation,
    pendingUserQuestion,
    pendingQuestionQueue,
    stageAnswer,
    getAllStagedAnswers,
    resetQuestionState,
    goToNextQuestion,
    goToPrevQuestion,
    streamsBySession,
    appendAssistantMessage,
    reconcileStaleStreamMessages,
    aiPlanStore
  }

  // ── 子 composable：会话生命周期 ──
  const {
    reset,
    resetAllState,
    setSession,
    clearSessionCache,
    attachToSession,
    ensureSessionIdBeforeMaterialUpload,
    recallUserMessageTurn,
    restoreV2PendingActions
  } = useSessionLifecycle(ctx)

  // ── 子 composable：流式编排（必须先于 pending 创建，因为 pending 依赖其恢复流） ──
  const {
    sendMessage,
    regenerateAssistantAfterUser,
    continueRun,
    resumeConfirmation,
    resumeUserQuestion,
    cancelStream
  } = useStreamOrchestrator(ctx)

  // ── 子 composable：待确认 / 待提问操作 ──
  const { confirmPendingConfirmation, cancelPendingConfirmation } = usePendingConfirmations(
    ctx,
    resumeConfirmation
  )
  const {
    answerPendingUserQuestion,
    stageAndGoNext,
    goToPrev
  } = usePendingUserQuestions(
    ctx,
    resumeUserQuestion
  )

  // ── 会话事件通道（useAiEvent → GET /api/ai/events）驱动的远程同步动作 ──

  /**
   * 移除一张已远程决议（过期/作废）的待办卡。
   *
   * <p>与 streamEvents.ts 的 pending_resolved 分支同源：按 pendingId 过滤统一队列；
   * 移除后该会话若已无任何在途任务（无本地流、无剩余待办），收敛侧边栏的"进行中"状态。</p>
   */
  function removePendingActionById(pendingId: string): void {
    const trimmed = pendingId?.trim() ?? ''
    if (!trimmed) return
    const removed = userActionQueue.value.find((x) => x.id === trimmed)
    if (!removed) return
    const sid = removed.conversationSessionId
    userActionQueue.value = userActionQueue.value.filter((x) => x.id !== trimmed)
    if (sid && !isSessionTaskOpen(sid)) {
      useAiSessionsStore().markEnded(sid)
    }
  }

  /**
   * 会话事件通道通知"该会话有新落库内容"（如卡片过期自动续跑完成、模型汇报已落库）时，
   * 静默重拉消息列表并合并，用户无需刷新即可看到最终回复。
   *
   * <p>仅在该会话正在被查看、且没有在途本地流时执行——本地流式增量是权威来源，
   * 静默重拉绝不与之互相覆盖。拉取期间用户切走/开始发送则丢弃本次结果。</p>
   */
  async function refreshMessagesSilently(targetSessionId: string): Promise<void> {
    const sid = targetSessionId?.trim() ?? ''
    if (!sid || sessionId.value?.trim() !== sid) return
    if (sending.value) return
    if (confirmationHttpPending.value) return
    if (streamsBySession.has(sid)) return
    try {
      const res = await aiChatApi.listMessages(sid)
      if (sessionId.value?.trim() !== sid) return
      if (sending.value || confirmationHttpPending.value || streamsBySession.has(sid)) return
      const rows = res.data ?? []
      messages.value = coalesceServerMessagesForUi(rows)
      messagesBySession.set(sid, [...messages.value])
      // 同步重拉待办权威快照：过期续跑可能产生新的链式卡（无推送），顺手补齐
      // （restoreV2PendingActions 只回填 PENDING 卡，已过期/已决议的不会回来）。
      await restoreV2PendingActions(sid)
    } catch {
      // 静默失败：刷新/切会话时的权威快照兜底路径不受影响。
    }
  }

  return {
    sessionId,
    messages,
    sending,
    awaitingUserAction,
    lastError,
    pendingConfirmation,
    pendingUserQuestion,
    pendingQuestionQueue,
    questionProgress,
    isLastQuestion,
    canGoPrevQuestion,
    currentStagedAnswer,
    recoverableStreamState,
    resumableStop,
    pendingConfirmationForActiveSession,
    pendingUserQuestionForActiveSession,
    userActionQueue,
    confirmationHttpPending,
    hasMessages,
    isSessionTaskOpen,
    streamsBySession,
    reset,
    resetAllState,
    clearSessionCache,
    setSession,
    attachToSession,
    sendMessage,
    cancelStream,
    continueRun,
    ensureSessionIdBeforeMaterialUpload,
    recallUserMessageTurn,
    regenerateAssistantAfterUser,
    clearPendingConfirmation,
    cancelPendingConfirmation,
    confirmPendingConfirmation,
    answerPendingUserQuestion,
    stageAndGoNext,
    goToPrev,
    goToNextQuestion,
    stageAnswer,
    removePendingActionById,
    refreshMessagesSilently
  }
})

registerTenantScopedReset('aiChat', () => useAiChatStore().resetAllState())
